/**
 * Sprint 5B — Instellingen → Herinneringen.
 * Alle stages configureerbaar. Backend is single source of truth.
 * Wijzigingen worden geaudit door backend.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Bell, Globe, Info, Lock } from "lucide-react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import { Switch } from "../../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

const TIMEZONE_OPTIONS = [
  "Europe/Amsterdam", "UTC", "America/Curacao", "Europe/London",
  "Europe/Brussels", "Europe/Berlin",
];

const STAGE_LABELS = {
  pre_reminder: "3 dagen vóór vervaldatum — vriendelijke herinnering",
  on_due: "Op vervaldatum — neutrale betalingsherinnering",
  overdue_7: "7 dagen na vervaldatum — 1e formele herinnering",
  overdue_14: "14 dagen na vervaldatum — 2e duidelijke herinnering",
  escalation_30: "30 dagen na vervaldatum — interne escalatie (géén klantmail)",
};

const AVAILABLE_PLACEHOLDERS = [
  "{{customer_name}}", "{{invoice_number}}", "{{invoice_date}}",
  "{{due_date}}", "{{days_overdue}}", "{{invoice_total}}",
  "{{amount_paid}}", "{{amount_due}}", "{{currency}}",
];

export default function ReminderSettingsSection() {
  const { user } = useAuth();
  // RBAC — Owner/Admin have full R/W. Manager sees but cannot edit.
  // Medewerker never reaches this section (tab is hidden in InstellingenSection).
  const canEdit = ["owner", "admin"].includes(user?.role);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await adminApi.get("/admin/reminder-settings");
      setSettings(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateStage = (idx, field, value) => {
    setSettings((s) => {
      const newStages = [...s.stages];
      newStages[idx] = { ...newStages[idx], [field]: value };
      return { ...s, stages: newStages };
    });
  };

  const updateSetting = (field, value) => {
    setSettings((s) => ({ ...s, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        timezone: settings.timezone,
        cron_hour_local: parseInt(settings.cron_hour_local),
        auto_enabled: !!settings.auto_enabled,
        include_partially_paid: !!settings.include_partially_paid,
        internal_notify_recipients: settings.internal_notify_recipients || [],
        vervalt_binnenkort_days: parseInt(settings.vervalt_binnenkort_days || 3),
        stages: settings.stages.map((s) => ({
          key: s.key,
          offset_days: parseInt(s.offset_days),
          active: !!s.active,
          auto_send: !!s.auto_send,
          notify_internal: !!s.notify_internal,
          subject_nl: s.subject_nl,
          body_nl: s.body_nl,
          cta_label_nl: s.cta_label_nl || null,
        })),
      };
      const r = await adminApi.put("/admin/reminder-settings", payload);
      setSettings(r.data);
      toast.success("Herinnering-instellingen opgeslagen");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const notifyRecipientsRaw = useMemo(() => {
    return (settings?.internal_notify_recipients || []).join(", ");
  }, [settings]);

  if (busy || !settings) {
    return (
      <div className="p-8 flex items-center gap-2 text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Instellingen laden...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reminder-settings-section">
      {!canEdit && (
        <div
          className="border-l-4 border-amber-500 bg-amber-50 p-3 flex items-start gap-2"
          data-testid="reminder-settings-readonly-banner"
        >
          <Lock className="w-4 h-4 text-amber-700 mt-0.5" />
          <div className="text-xs text-amber-900">
            <div className="font-bold">Alleen-lezen weergave</div>
            <div>
              U heeft leestoegang tot deze instellingen. Aanpassingen mogen
              alleen door een Owner of Admin gedaan worden.
            </div>
          </div>
        </div>
      )}

      {/* Global settings */}
      <div className="border border-gray-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-3">
          <Globe className="w-4 h-4 text-[#0055FF]" />
          <h2 className="text-lg font-chivo font-bold text-[#0A192F]">
            Algemene planning
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Timezone</Label>
            <Select
              value={settings.timezone}
              onValueChange={(v) => updateSetting("timezone", v)}
              disabled={!canEdit}
            >
              <SelectTrigger
                className="rounded-none mt-1"
                data-testid="reminder-settings-timezone"
                disabled={!canEdit}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONE_OPTIONS.map((tz) => (
                  <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Dagelijkse verwerking om (uur, lokaal)</Label>
            <Input
              type="number" min="0" max="23"
              value={settings.cron_hour_local}
              onChange={(e) => updateSetting("cron_hour_local", e.target.value)}
              className="rounded-none mt-1"
              data-testid="reminder-settings-cron-hour"
              disabled={!canEdit}
              readOnly={!canEdit}
            />
          </div>
          <div>
            <Label className="text-xs">
              Drempel &quot;vervalt binnenkort&quot; (dagen)
            </Label>
            <Input
              type="number" min="1" max="30"
              value={settings.vervalt_binnenkort_days || 3}
              onChange={(e) => updateSetting("vervalt_binnenkort_days", e.target.value)}
              className="rounded-none mt-1"
              data-testid="reminder-settings-vervalt-binnenkort"
              disabled={!canEdit}
              readOnly={!canEdit}
            />
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Switch
              checked={!!settings.auto_enabled}
              onCheckedChange={(v) => updateSetting("auto_enabled", v)}
              data-testid="reminder-settings-auto-enabled"
              disabled={!canEdit}
            />
            <Label className="text-xs">Automatische dagelijkse verwerking</Label>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Switch
              checked={!!settings.include_partially_paid}
              onCheckedChange={(v) => updateSetting("include_partially_paid", v)}
              data-testid="reminder-settings-include-partial"
              disabled={!canEdit}
            />
            <Label className="text-xs">Deels betaalde facturen meenemen</Label>
          </div>
        </div>
        <div className="mt-3">
          <Label className="text-xs">
            Interne notificatie-ontvangers (komma gescheiden e-mailadressen)
          </Label>
          <Input
            defaultValue={notifyRecipientsRaw}
            onChange={(e) => updateSetting(
              "internal_notify_recipients",
              e.target.value.split(/[,;\n]+/).map((x) => x.trim()).filter(Boolean),
            )}
            className="rounded-none mt-1"
            placeholder="owner@5999cargo.nl, admin@5999cargo.nl"
            data-testid="reminder-settings-internal-recipients"
            disabled={!canEdit}
            readOnly={!canEdit}
          />
        </div>
      </div>

      {/* Stages */}
      <div className="space-y-4">
        {settings.stages.map((stage, idx) => (
          <div
            key={stage.key}
            className="border border-gray-200 bg-white p-4"
            data-testid={`reminder-settings-stage-${stage.key}`}
          >
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-[#0055FF]" />
                <div>
                  <div className="font-chivo font-bold text-sm text-[#0A192F]">
                    {STAGE_LABELS[stage.key] || stage.key}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    Fase-key: <code>{stage.key}</code>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={!!stage.active}
                  onCheckedChange={(v) => updateStage(idx, "active", v)}
                  data-testid={`reminder-settings-stage-${stage.key}-active`}
                  disabled={!canEdit}
                />
                <Label className="text-xs">Actief</Label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <Label className="text-xs">Offset dagen (negatief = vóór due-date)</Label>
                <Input
                  type="number"
                  value={stage.offset_days}
                  onChange={(e) => updateStage(idx, "offset_days", e.target.value)}
                  className="rounded-none mt-1"
                  data-testid={`reminder-settings-stage-${stage.key}-offset`}
                  disabled={!canEdit}
                  readOnly={!canEdit}
                />
              </div>
              <div className="flex items-center gap-2 md:mt-6">
                <Switch
                  checked={!!stage.auto_send}
                  onCheckedChange={(v) => updateStage(idx, "auto_send", v)}
                  data-testid={`reminder-settings-stage-${stage.key}-auto-send`}
                  disabled={!canEdit}
                />
                <Label className="text-xs">
                  Automatisch verzenden naar klant
                </Label>
              </div>
              <div className="flex items-center gap-2 md:mt-6">
                <Switch
                  checked={!!stage.notify_internal}
                  onCheckedChange={(v) => updateStage(idx, "notify_internal", v)}
                  data-testid={`reminder-settings-stage-${stage.key}-notify-internal`}
                  disabled={!canEdit}
                />
                <Label className="text-xs">Interne notificatie versturen</Label>
              </div>
            </div>

            <div className="mb-3">
              <Label className="text-xs">Onderwerp</Label>
              <Input
                value={stage.subject_nl}
                onChange={(e) => updateStage(idx, "subject_nl", e.target.value)}
                className="rounded-none mt-1"
                data-testid={`reminder-settings-stage-${stage.key}-subject`}
                disabled={!canEdit}
                readOnly={!canEdit}
              />
            </div>
            <div className="mb-3">
              <Label className="text-xs">E-mailtekst</Label>
              <Textarea
                rows={8}
                value={stage.body_nl}
                onChange={(e) => updateStage(idx, "body_nl", e.target.value)}
                className="rounded-none mt-1 font-mono text-xs"
                data-testid={`reminder-settings-stage-${stage.key}-body`}
                disabled={!canEdit}
                readOnly={!canEdit}
              />
            </div>
            <div className="mb-2">
              <Label className="text-xs">CTA-label (optioneel)</Label>
              <Input
                value={stage.cta_label_nl || ""}
                onChange={(e) => updateStage(idx, "cta_label_nl", e.target.value || null)}
                className="rounded-none mt-1"
                data-testid={`reminder-settings-stage-${stage.key}-cta`}
                disabled={!canEdit}
                readOnly={!canEdit}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Placeholder helper */}
      <div className="border border-blue-200 bg-blue-50 p-3">
        <div className="flex items-center gap-2 text-blue-900 text-xs font-bold mb-2">
          <Info className="w-3 h-3" /> Beschikbare placeholders
        </div>
        <div className="flex flex-wrap gap-1">
          {AVAILABLE_PLACEHOLDERS.map((p) => (
            <code
              key={p}
              className="text-[11px] bg-white px-2 py-0.5 border border-blue-300 text-blue-900"
            >
              {p}
            </code>
          ))}
        </div>
      </div>

      {/* Save */}
      {canEdit && (
        <div className="flex justify-end sticky bottom-0 bg-white/95 backdrop-blur border-t border-gray-200 py-3">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="rounded-none bg-[#0055FF] hover:bg-[#0A192F] text-white"
            data-testid="reminder-settings-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Instellingen opslaan
          </Button>
        </div>
      )}
    </div>
  );
}
